import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Field} from 'redux-form';
import {RESOURCES as locationResources} from '../fuseLocationsResourceMap';
import {RESOURCES as productResources} from '../fuseProductsResourceMap';
import {sortArrayByKey} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {MONTHS_NUMBERS, YEARS} from '../shared/dateTimeLists';
import {REPORT_JOB_LIST_TYPES} from '../shared/reportJobLists';
import {US_STATES} from '../shared/usStates';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';

/**
 * A field containing a dynamic Virtualized Select component
 *
 */
class JobListSelectField extends Component {
    constructor(props) {
        super(props);
        let itemList = [];
        // Pre-populate list if static
        if (props.customType === 'Month') {
            itemList = this.processListData(MONTHS_NUMBERS);
        }
        if (props.customType === 'States') {
            let states = [];
            US_STATES.forEach((item, idx) => {
                states.push({id: item.abbreviation, name: item.name});
            });
            states = sortArrayByKey(states, 'id', 'ASC');
            itemList = this.processListData(states);
        }
        if (props.customType === 'Year') {
            itemList = this.processListData(YEARS);
        }
        // Pre-populate selection if a default value is provided
        let selectedItem = null;
        if (props.defaultValue && itemList.length > 0) {
            const item = itemList.find(r => r[props.returnKey].toString() === props.defaultValue.toString());
            if (item) {
                selectedItem = item;
            }
        }
        this.state = {
            itemList: itemList,
            selectedItem: selectedItem,
        };
        this.handleItemSelect = this.handleItemSelect.bind(this);
    }

    componentDidMount() {
        const customType = this.props.customType;
        const selectedItem = this.state.selectedItem;
        if (customType === 'Month' || customType === 'States' || customType === 'Year') {
            if (this.props.defaultValue && this.state.itemList.length > 0) {
                if (this.props.selectionType === 'object') {
                    this.props.onSelectItem(customType, selectedItem);
                }
                if (this.props.selectionType === 'id') {
                    this.props.onSelectItem(customType, selectedItem.id.toString());
                }
            }
        } else {
            // Retrieve data for lists
            this.fetchListData();
        }
    }

    /**
     * Get list based on type
     */
    fetchListData() {
        const type = this.props.customType;
        let params = {
            filter: {},
            pagination: {page: 1, perPage: 10000},
            sort: {field: 'id', order: 'ASC'}
        };
        let resource = locationResources.LocationsMin.GET_LIST(params);
        if (type === 'DBA' || type === 'DBAs') {
            resource = locationResources.DBAs.GET_LIST(params);
        } else if (type === 'Cities') {
            resource = locationResources.LocationCities.GET_LIST(params);
        } else if (type === 'District' || type === 'Districts') {
            resource = locationResources.Districts.GET_LIST(params);
        } else if (type === 'Location' || type === 'Locations') {
            resource = locationResources.LocationsMin.GET_LIST(params);
        } else if (type === 'LocationType' || type === 'LocationTypes') {
            resource = locationResources.LocationTypes.GET_LIST(params);
        } else if (type === 'ProductType' || type === 'ProductTypes') {
            resource = productResources.ProductTypes.GET_LIST(params);
        }

        // Retrieve data and assign results to the Select choices
        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            // Most results are a data payload
            let payload = (result.data && result.data.payload) ? result.data.payload : {};
            // Cities results are the data
            if (type === 'Cities') {
                payload = result.data;
            }
            if (payload) {
                const list = this.processListData(payload);
                let selectedItem = this.state.selectedItem;
                let defaultValue = this.props.defaultValue;
                // If a default value exists, set it if available
                if (defaultValue) {
                    const item = list.find(r => r[this.props.returnKey].toString() === defaultValue.toString());
                    if (item) {
                        selectedItem = item;
                    }
                }
                this.setState({
                    itemList: list,
                    selectedItem: selectedItem
                }, () => {
                    if (selectedItem) {
                        if (this.props.selectionType === 'object') {
                            this.props.onSelectItem(this.props.customType, selectedItem);
                        }
                        if (this.props.selectionType === 'id') {
                            this.props.onSelectItem(this.props.customType, selectedItem.id);
                        }
                    }
                });
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /**
     * Format display names for select component
     *
     * @param list
     * @return {Array}
     */
    processListData(list) {
        let itemList = [];
        const type = this.props.customType;

        if (list && list.length > 0) {
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                if (item) {
                    // Convert ID to a string or an integer based on Custom Type
                    if (item.id) {
                        if (REPORT_JOB_LIST_TYPES[type] && REPORT_JOB_LIST_TYPES[type] === 'int') {
                            item.id = parseInt(item.id, 10);
                        } else {
                            item.id = item.id.toString();
                        }
                    }
                    const name = (item.name) ? item.name : '';
                    // Assemble display name based on type
                    item.display_name = (item.id) ? item.id.toString() + ' - ' + name : name;
                    if (type === 'Cities') {
                        item.id = item.city;
                        item.display_name = item.city + ((item.state) ? ', ' + item.state : '');
                    } else if (type === 'Location' || type === 'Locations') {
                        const city = (item.city) ? ' - ' + item.city : '';
                        const state = (item.state) ? ' ' + item.state : '';
                        item.display_name = item.id.toString() + ' - ' + name + city + state;
                    } else if (type === 'Month' || type === 'ProductType' || type === 'Year') {
                        item.display_name = name;
                    } else {
                        item.display_name = item.id.toString() + ' - ' + name;
                    }
                    itemList.push(item);
                }
            }
        }
        return itemList;
    }

    /**
     * Handle selection event and notify parent
     *
     * @param event
     * @param index
     * @param value
     */
    handleItemSelect(event, index, value) {
        const type = this.props.customType;
        if (index && index !== value) {
            // Extract the full object from the selected id
            const obj = this.state.itemList.find(n => n.id.toString() === index.toString());
            if (obj) {
                this.setState({
                    selectedItem: obj,
                }, () => {
                    if (this.props.selectionType === 'object') {
                        this.props.onSelectItem(type, obj);
                    }
                    if (this.props.selectionType === 'id') {
                        this.props.onSelectItem(type, obj.id);
                    }
                });
            }
        } else if (!index) {
            this.setState({
                selectedItem: null,
            }, () => {
                if (this.props.selectionType === 'object') {
                    this.props.onSelectItem(type, null);
                }
                if (this.props.selectionType === 'id') {
                    this.props.onSelectItem(type, '');
                }
            });
        }
    }

    render() {
        const {
            label,
            param,
            required
        } = this.props;

        const {
            itemList,
            selectedItem
        } = this.state;

        if (!itemList || itemList.length === 0) {
            return (
                <Fragment>
                    <Field name={`${param}_empty_id`}
                           component={VirtualizedSelectInput}
                           choices={[]}
                           label={`${label}${(required) ? ' *' : ''}`}
                           optionText="display_name"
                           optionValue="id"
                           required={required}
                           allowEmpty
                           options={{
                               className: "has-label inline two-thirds-width",
                               clearable: true,
                               placeholder: `${label}${(required) ? ' *' : ''}`
                           }}
                    />
                </Fragment>
            )
        }

        return (
            <Fragment>
                <Field name={`${param}_id`}
                       component={VirtualizedSelectInput}
                       choices={itemList}
                       label={`${label}${(required) ? ' *' : ''}`}
                       optionText="display_name"
                       optionValue="id"
                       required={required}
                       onChange={this.handleItemSelect}
                       allowEmpty
                       options={{
                           className: "has-label inline two-thirds-width",
                           clearable: true,
                           placeholder: `${label}${(required) ? ' *' : ''}`
                       }}
                       defaultValue={(selectedItem) ? selectedItem : undefined}
                />
            </Fragment>
        );
    }
}

JobListSelectField.propTypes = {
    customType: PropTypes.string,
    defaultValue: PropTypes.any,
    label: PropTypes.string,
    param: PropTypes.string,
    onSelectItem: PropTypes.func,
    required: PropTypes.bool,
    returnKey: PropTypes.string,
    selectionType: PropTypes.string,
    tooltip: PropTypes.string,
    value: PropTypes.any
};

JobListSelectField.defaultProps = {
    param: 'locations',
    required: false,
    returnKey: 'id',
    selectionType: 'object'
};

export default JobListSelectField;



// WEBPACK FOOTER //
// ./src/Fuse/components/JobListSelectField.js